import React, { useState } from 'react';
import { Info, Shield, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface MessageMetadata {
  confidence?: number;
  safety_active?: boolean;
  response_time?: number;
  tool_used?: string;
}

interface MessageMetadataBadgeProps {
  metadata: MessageMetadata;
  className?: string;
}

/**
 * Displays a badge with confidence score that expands to show full metadata
 */
export const MessageMetadataBadge: React.FC<MessageMetadataBadgeProps> = ({
  metadata,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { confidence, safety_active, response_time, tool_used } = metadata;

  // Don't render if no metadata
  if (!confidence && !safety_active && !response_time && !tool_used) {
    return null;
  }

  // Determine confidence color
  const getConfidenceColor = (conf: number) => {
    if (conf >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (conf >= 70) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-orange-600 bg-orange-50 border-orange-200';
  };

  const confidenceColorClass = confidence ? getConfidenceColor(confidence) : '';

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-6 px-2 py-0.5 text-xs rounded-full border transition-all',
            confidenceColorClass,
            'hover:shadow-sm',
            className
          )}
        >
          <Info className="w-3 h-3 mr-1" />
          {confidence !== undefined && `${confidence}% confident`}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-64 p-3 bg-white border shadow-lg rounded-lg"
      >
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">
            Response Metadata
          </h4>

          {confidence !== undefined && (
            <div className="flex items-center gap-2 text-sm">
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center',
                confidence >= 90 ? 'bg-green-100' : confidence >= 70 ? 'bg-yellow-100' : 'bg-orange-100'
              )}>
                <Zap className={cn(
                  'w-4 h-4',
                  confidence >= 90 ? 'text-green-600' : confidence >= 70 ? 'text-yellow-600' : 'text-orange-600'
                )} />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Confidence</p>
                <p className="font-medium text-gray-900">{confidence}%</p>
              </div>
            </div>
          )}

          {safety_active !== undefined && (
            <div className="flex items-center gap-2 text-sm">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <Shield className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Safety Guardrails</p>
                <p className="font-medium text-gray-900">
                  {safety_active ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
          )}

          {response_time !== undefined && (
            <div className="flex items-center gap-2 text-sm">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <Zap className="w-4 h-4 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Response Time</p>
                <p className="font-medium text-gray-900">{response_time}s</p>
              </div>
            </div>
          )}

          {tool_used && (
            <div className="flex items-center gap-2 text-sm">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <Info className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Tool Used</p>
                <p className="font-medium text-gray-900">{tool_used}</p>
              </div>
            </div>
          )}

          {/* Info text */}
          <div className="pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              This metadata provides transparency into how the AI generated this response.
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
